import { useEffect, useState } from 'react';

import { GridRowModes, GridToolbarContainer, GridActionsCellItem } from '@mui/x-data-grid-premium';
import { Field, useFormikContext, FieldArray } from 'formik';
import { dataGridPremiumColumns } from '../../utils/helper';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {
  DataGridIdentifiers,
  FormErrorUI,
  InputUI,
  AddButtonUi,
  ColorCircleUi,
  ChromeColorPickerUI,
  DatagridPremiumUi,
} from '../Interface';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { Popover } from '@mui/material';

function EditToolbar(props) {
  const { disabled, push, defPropertyOption } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newRow = {
      ...defPropertyOption,
      id: uuidv4(),
    };

    await push(newRow);

    const fieldsToValidate = [
      `propertyOption.${values.propertyOption.length ?? 0}.name`,
      `propertyOption.${values.propertyOption.length ?? 0}.color`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const SelectListDatagrid = ({ disabled, defPropertyOption }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const [anchorEl, setAnchorEl] = useState(null);

  const getValueIndex = (values, id) => {
    return values.propertyOption.findIndex((rc) => rc.id === id);
  };

  const handlePopoverOpen = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'color-popover' : undefined;

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Megnevezés',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`propertyOption.${valueIndex}.name`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.propertyOption?.[valueIndex]?.name} />}
              onChange={(e) => {
                setFieldValue(`propertyOption.${valueIndex}.name`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`propertyOption.${valueIndex}.name`)}
            />
          );
        },
      },
      {
        field: 'color',
        headerName: 'Szín',
        type: 'string',
        editable: true,
        renderCell: (params) => {
          return <ColorCircleUi color={params.value ?? '#FFFFFF'} size={20} />;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <div className="ml-1 w-[100%] flex justify-center">
              <div
                className={`${!disabled ? 'cursor-pointer' : 'cursor-not-allowed'} rounded-full shadow-[rgba(0,_0,_0,_0.54)_0px_1px_8px] w-[20px] h-[20px] mt-2.5`}
                style={{ background: values.propertyOption[valueIndex].color }}
                onClick={handlePopoverOpen}
              />
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'center',
                  horizontal: 'left',
                }}
              >
                <ChromeColorPickerUI
                  color={values.propertyOption[valueIndex].color}
                  onChange={(color) => {
                    setFieldValue(`propertyOption.${valueIndex}.color`, color.hex);
                    data.api.setEditCellValue({
                      id: data.id,
                      field: data.field,
                      value: color.hex,
                    });
                  }}
                />
              </Popover>
            </div>
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="propertyOption">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.SelectListDatagrid}
                datagridInitialState={{ density: 'compact' }}
                height={608}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.propertyOption ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                headerFilters={false}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defPropertyOption,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                editMode="row"
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default SelectListDatagrid;
